import React, {createRef} from 'react'
import {findDOMNode} from 'react-dom'

import Popover from "../../../gd-components/gd-popover/popover-v2"
import PopoverPosition from "../../../gd-components/gd-popover/popover-position"
import {PopoverAttachmentType} from "../../../gd-components/gd-popover/@const"
import VScroll from '../../../gd-components/scrolling/custom-v-scroll/v-scroll'

import Search from "../../../gd-components/popover-menu/search/search"
import UserIcon from '../../../gd-components/icons/user/user-icon-deprecated'
import {CUSTOM_TYPE, NO_VALUE_TYPE, USER_TYPE} from '../select-user'

type Props = {
    searchString?           : string
    currentCursorKey        : number

    options                 : ID[]          // array of user IDs
    value?                  : ID
    values                  : ID[]          // array of user IDs
    additionalOptions?      : any[]         // array of objects to add to options, first usage to add "Not assigned" option

    canHaveNoValue?         : boolean       // if true we have support "no value" as a selector
    noValueLabel?           : string        // Text that will appear for "no-value" option

    addUserLabel?           : string        // if passed we'll show "add user" option
    addUserCallback?        : func          // add user callback

    infoMessage?            : any           // popover bottom info message

    // popover options
    popoverFitTargetWidth?  : boolean       // if true popover width matches selector (value) width
    popoverStyle?           : any           // special styles to popover

    renderValue?            : 'name' | 'icon-name' | 'icon' | 'inline' | 'form-icon-name' | 'form' | 'control' | 'func'

    attachmentType?         : 'full' | 'vertical' | 'horizontal'
    attachment?             : string
    targetAttachment?       : string
    targetOffset?           : string
    popoverClass?           : string        // if passed will add this class to popover


    multiselect?            : boolean       // allow to select multiple users
    keyboardNavigation?     : boolean       // navigate with keyboard up down enter
    searchable?             : boolean       // display quick serach input

    onChangeCursorKey       : func
    onOptionSelected        : func
    onSearchChange          : func
    onAddUserClick?         : func
    onHidePopover           : func
}

export default class GDSelectUserPopover extends React.Component<Props> {

    private optionRef: any
    private scrollRef: any = createRef()

    constructor(props: Props) {
        super(props)

        this.optionRef = {}
    }

    componentDidUpdate(prevProps: Props, prevState: any, snapshot: any) {
        const node: any = this.optionRef[this.props.currentCursorKey] ? findDOMNode(this.optionRef[this.props.currentCursorKey]) : null
        const scrollNode: any = findDOMNode(this.scrollRef.current.childrenContainer)

        if (node) {
            const nodeTop       = node.offsetTop
            const nodeBottom    = node.offsetTop + node.offsetHeight
            const scrollTop     = scrollNode.scrollTop
            const scrollBottom  = scrollNode.scrollTop + scrollNode.offsetHeight
            if (scrollBottom < nodeBottom) this.scrollRef.current.setScrollTop(nodeBottom - scrollNode.offsetHeight, false, 100)
            else if (scrollTop > nodeTop) this.scrollRef.current.setScrollTop(nodeTop, false, 100)
        }
    }

    _getOptions = () => {
        // 4 types of options: add, no-value, user, custom
        const selectOptions = []

        let searchString = this.props.searchString
        if (searchString) searchString = searchString.toLowerCase()

        if (this.props.canHaveNoValue) {
            // make sure filter applies (search)
            if (!searchString || this.props.noValueLabel?.toLowerCase().includes(searchString)) {
                selectOptions.push({
                    type: NO_VALUE_TYPE
                })
            }
        }

        const options = _.union(this.props.options, this.props.values)

        if (!_.isEmpty(options)) {
            let users = _(options).map(userId=>gd.session.users.get(userId)).compact().value()
            if (searchString) {
                users = _.filter(users,user => {
                    return user.name.toLowerCase().includes(searchString!)
                })
            }

            this.props.additionalOptions && this.props.additionalOptions.map(user => {
                selectOptions.push({
                    type: CUSTOM_TYPE,
                    user: user
                })
            })

            // now add users to options
            users.map(user=>{
                selectOptions.push({
                    type: USER_TYPE,
                    user: user
                })
            })
        }

        return selectOptions
    }

    onPopoverKeyDown = (event: React.KeyboardEvent) => {
        if (!this.props.keyboardNavigation) return

        const { currentCursorKey } = this.props
        const selectOptions = this._getOptions()

        let newValue

        switch(event.key) {
            case 'Escape'       :
                this.props.onHidePopover()
                break

            case 'ArrowUp'      :
                if (currentCursorKey==null) {
                    newValue = 0
                } else {
                    newValue = currentCursorKey - 1
                    if (newValue < 0) newValue = 0
                }

                this.props.onChangeCursorKey(newValue)
                break

            case 'ArrowDown'    :
                if (currentCursorKey==null) {
                    newValue = 0
                } else {
                    newValue = currentCursorKey + 1
                    if (newValue > selectOptions.length - 1) newValue = selectOptions.length - 1
                }

                this.props.onChangeCursorKey(newValue)
                break

            case 'Enter'        :
                if (currentCursorKey!=null) {
                    const curOption = selectOptions[currentCursorKey]
                    if (curOption) this.onOptionSelected(curOption)
                }
                break
        }
    }

    onOptionSelected = (value: any) => {
        this.props.onOptionSelected(value)
    }

    renderNoValue(key: number, option: any) {
        const cursorHighLighted = key === this.props.currentCursorKey
        const classes = Classes('option with-icon-26 no-value', {
            'cursor-highlighted': cursorHighLighted
        })

        return (
            <div ref={r=>this.optionRef[key] = r} key={key} className={classes} onClick={this.onOptionSelected.bind(this,option)}>
                <div className="ico i5-users"/>
                <div className="label">{this.props.noValueLabel}</div>
            </div>
        )
    }

    renderUserType(key: number, option: any) {
        const isSelected = this.props.value === option.user.id || _.includes(this.props.values, option.user.id)
        const cursorHighLighted = key === this.props.currentCursorKey
        const classes = Classes('option with-icon-26', {
            'selected'              : !this.props.multiselect && isSelected,
            'cursor-highlighted'    : cursorHighLighted,
            'multiselect'           : this.props.multiselect
        })

        return (
            <div ref={r=>this.optionRef[key] = r} key={key} className={classes} onClick={this.onOptionSelected.bind(this,option)}>
                {this.props.multiselect && <div className={Classes("ui5-cb s-13", {selected:isSelected})}/>}
                <div className="ico"><UserIcon user={option.user} hint={false} size="26"/></div>
                <div className="label">{option.user.name}</div>
            </div>
        )
    }

    renderCustomType(key: number, option: any) {
        const cursorHighLighted = key === this.props.currentCursorKey
        const classes = Classes('option with-icon-26 custom-value', {
            'cursor-highlighted'    : cursorHighLighted,
            'multiselect'           : this.props.multiselect
        })

        const isSelected2 = _.includes(this.props.values, option.user.id)
         return (
            <div ref={r=>this.optionRef[key] = r} key={key} className={classes} onClick={this.onOptionSelected.bind(this,option)}>
                {this.props.multiselect && <div className={Classes("ui5-cb", "s-13", {selected:isSelected2})}/>}
                <span className="ico i5-user custom-icon"/>
                <div className="label">{option.user.name}</div>
            </div>
        )
    }

    render() {
        let content: any = []
        const selectOptions = this._getOptions()

        if (selectOptions.length>0) {
            selectOptions.map((option,key)=>{
                switch (option.type) {
                    case NO_VALUE_TYPE  : content.push(this.renderNoValue(key, option)); break
                    case USER_TYPE      : content.push(this.renderUserType(key, option)); break
                    case CUSTOM_TYPE    : content.push(this.renderCustomType(key, option)); break
                }
            })
        } else {
            content = <div className="option with-icon-26">No users found</div>
        }

        let searchRes
        if (this.props.searchable && (selectOptions.length >= 5 || this.props.searchString)) {
            searchRes = <Search searchString={this.props.searchString} onSearch={this.props.onSearchChange} onEscapePressed={this.props.onHidePopover}/>
        } else {
            // still need to autofocus so keyboard work
            searchRes = <span className="hidden-search-input" tabIndex={0}/>
        }

        let addUser: any = null
        if (this.props.addUserLabel && this.props.addUserCallback) {
            addUser = (
                <div className="option with-icon-26" onClick={this.props.onAddUserClick}>
                    <div className="ico i5-plus"/>
                    <div className="label">{this.props.addUserLabel}</div>
                </div>
            )
        }

        const help = this.props.infoMessage ? <div className="help">{this.props.infoMessage}</div> : null

        const popoverPosition = new PopoverPosition({
            attachmentType  : this.props.attachmentType as PopoverAttachmentType,
            attachment      : this.props.attachment,
            targetAttachment: this.props.targetAttachment,
            targetOffset    : this.props.targetOffset,
        })

        return (
            <Popover popoverClass     = {Classes('gd-select-user-popover min-w-250', this.props.popoverClass, 'render-'+this.props.renderValue)}
                     targetClass        = "pop-target"
                     position           = {popoverPosition}
                     onKeyDown          = {this.onPopoverKeyDown}
                     onClickOutside     = {this.props.onHidePopover}
                     fitTargetWidth     = {this.props.popoverFitTargetWidth}
                     style              = {this.props.popoverStyle}>

                {searchRes}

                <VScroll ref={this.scrollRef}>
                    <div className={Classes('ui5-popover-menu', {'with-help': !!help})}>
                        {content}
                        {addUser}
                        {help}
                    </div>
                </VScroll>
            </Popover>
        )
    }
}